import {
  getInclusionOptions,
  getOperatorOptions,
  getEventHistoryOperatorOptions,
  getPurchaseHistoryOperatorOptions,
  getMathematicalOperatorOptions
} from '../logicHelpers'
import { getIntegrationData } from '@dashboard/modules/integration/services/integrationDataUtilities'
import type { Partner } from '@dashboard/types/partner'

/**
 * Creates condition checking functions for form inputs
 * @param partnerName The name of the partner to check against
 */
const createConditionCheckers = (partnerName: string) => ({
  isTagsOrLists: (condition: { $source: string; $type: string }) =>
    condition.$source === partnerName && (condition.$type === 'tags' || condition.$type === 'lists'),

  isCustomField: (condition: { $source: string; $type: string }) =>
    condition.$source === partnerName && condition.$type === 'customField',

  isTags: (condition: { $source: string; $type: string }) =>
    condition.$source === partnerName && condition.$type === 'tags',

  isLists: (condition: { $source: string; $type: string }) =>
    condition.$source === partnerName && condition.$type === 'lists',

  isEventHistory: (condition: { $source: string; $type: string }) =>
    condition.$source === partnerName && condition.$type === 'eventHistory',

  isPurchaseHistory: (condition: { $source: string; $type: string }) =>
    condition.$source === partnerName && condition.$type === 'purchaseHistory',

  isTotalRevenue: (condition: { $source: string; $type: string }) =>
    condition.$source === partnerName && condition.$type === 'totalRevenue'
})

const makePartnerSource = (partner?: Partner) => {
  const partnerName = partner?.name || 'espplaceholder'
  const conditions = createConditionCheckers(partnerName)
  const support = partner?.espFeatureSupport
  const tagSupport = typeof support?.tag === 'object' ? support.tag : undefined
  const fieldSupport = typeof support?.customField === 'object' ? support.customField : undefined
  const listSupport = typeof support?.list === 'object' ? support.list : undefined
  const platform = partner?.label || 'your email platform'

  return {
    types: [
      {
        value: 'isAnonymous',
        label: 'Anonymous',
        description: `Not a known ${platform} contact`
      },
      {
        value: 'isSubscriber',
        label: 'Subscriber',
        description: `A known ${platform} contact`
      },
      ...(partner?.espFeatureSupport.tag
        ? [
            {
              value: 'tags',
              label: tagSupport?.label || 'Tags',
              description: 'Tags on their contact'
            }
          ]
        : []),
      ...(partner?.espFeatureSupport.customField
        ? [
            {
              value: 'customField',
              label: fieldSupport?.label || 'Custom Field',
              description: 'A field on their contact'
            }
          ]
        : []),
      ...(partner?.espFeatureSupport.list
        ? [
            {
              value: 'lists',
              label: listSupport?.label || 'Lists',
              description: 'Lists their contact is on'
            }
          ]
        : []),
      ...(partner?.espFeatureSupport?.eventHistory
        ? [
            {
              value: 'eventHistory',
              label: partner?.espFeatureSupport.eventHistory.label || 'Event History',
              description: 'Events recorded for their contact'
            }
          ]
        : []),
      ...(partner?.espFeatureSupport?.purchaseHistory
        ? [
            {
              value: 'purchaseHistory',
              label: partner?.espFeatureSupport.purchaseHistory.label || 'Purchase History',
              description: "What they've bought"
            }
          ]
        : []),
      ...(partner?.espFeatureSupport?.purchaseHistory?.supportsRevenue
        ? [
            {
              value: 'totalRevenue',
              label: 'Total Revenue',
              description: "How much they've spent"
            }
          ]
        : [])
    ],

    inputs: [
      {
        control: 'select',
        name: 'operator',
        key: 'operator-select',
        options: getInclusionOptions(),
        can: conditions.isTagsOrLists
      },
      {
        // Rendered by SyncUpField: the same searchable field list as sync and Set Data destinations.
        control: fieldSupport?.unstructured ? 'text' : 'customField',
        name: 'customFieldId',
        key: 'custom-field-field',
        can: conditions.isCustomField
      },
      {
        control: 'autocomplete',
        name: 'tagIds',
        key: 'tag-ids',
        type: 'tags',
        placeholder: `Choose ${(tagSupport?.label || 'Tags').toLowerCase()}`,
        options: async () => {
          const data = await getIntegrationData('partner', 'tags')
          return data.value.map((field) => ({
            label: field.label,
            value: String(field.id)
          }))
        },
        multiple: true,
        can: conditions.isTags
      },
      {
        control: 'autocomplete',
        name: 'listIds',
        key: 'list-ids',
        placeholder: `Choose ${(listSupport?.label || 'Lists').toLowerCase()}`,
        options: async () => {
          const data = await getIntegrationData('partner', 'lists')
          return data.value.map((list) => ({
            label: list.label,
            value: String(list.id)
          }))
        },
        multiple: true,
        can: conditions.isLists
      },
      {
        control: 'select',
        key: 'custom-field-operator-select',
        name: 'operator',
        options: getOperatorOptions(),
        can: (condition: { $source: string; $type: string }) =>
          condition.$source === partnerName && condition.$type === 'customField'
      },
      {
        control: 'text',
        key: 'custom-field-value-input',
        name: 'value',
        can: (condition: { $source: string; $type: string; operator: string }) =>
          condition.$source === partnerName &&
          condition.$type === 'customField' &&
          condition.operator !== 'is set' &&
          condition.operator !== 'is not set',
        // The snippet's customField evaluator short-circuits to false when the
        // ESP returns an empty/null value (`partner.js:909`: `!!customFieldValue
        // && ...`). A rule authored with `equals`/`contains`/etc. and a blank
        // value is therefore dead config — it can never match. Surface that in
        // the condition builder so authors don't silently break their own
        // targeting; the correct construct for "field is empty" is the
        // `is not set` operator, which bypasses the guard (see case
        // `'is set'/'is not set'` at `partner.js:907`).
        validate: (value: string | null | undefined) => {
          if (value === undefined || value === null || value === '') {
            return "Blank value won't match. Use the 'is not set' operator to match missing values."
          }
          return null
        }
      },
      // Event History inputs
      {
        control: 'autocomplete',
        name: 'eventName',
        key: 'event-name-select',
        placeholder: 'Select event...',
        options: async () => {
          const data = await getIntegrationData('partner', 'available_events')
          return (data.value || []).map((event: { name: string }) => ({
            label: event.name,
            value: event.name
          }))
        },
        can: conditions.isEventHistory
      },
      {
        control: 'select',
        name: 'operator',
        key: 'event-history-operator-select',
        value: 'has_fired',
        options: getEventHistoryOperatorOptions(),
        can: conditions.isEventHistory
      },
      {
        control: 'number',
        name: 'count',
        key: 'event-count-input',
        placeholder: 'Count',
        min: 0,
        can: (condition: { $source: string; $type: string; operator: string }) =>
          condition.$source === partnerName &&
          condition.$type === 'eventHistory' &&
          ['gte', 'lt', 'equals'].includes(condition.operator)
      },
      // Purchase History inputs - operator first, then product (conditionally)
      {
        control: 'select',
        name: 'operator',
        key: 'purchase-history-operator-select',
        value: 'include any',
        options: getPurchaseHistoryOperatorOptions(),
        can: conditions.isPurchaseHistory
      },
      {
        control: 'autocomplete',
        name: 'productIds',
        key: 'product-ids-select',
        placeholder: 'Select product(s)...',
        options: async () => {
          const data = await getIntegrationData('partner', 'available_products')
          const products = ('products' in data.value && data.value.products) || []
          return products.map((productId: string) => ({
            label: productId,
            value: productId
          }))
        },
        multiple: true,
        // Only show product dropdown for specific product operators (not "is set" / "is not set")
        can: (condition: { $source: string; $type: string; operator: string }) =>
          condition.$source === partnerName &&
          condition.$type === 'purchaseHistory' &&
          ['include any', 'do not include any'].includes(condition.operator)
      },
      // Total Revenue inputs
      {
        control: 'select',
        name: 'operator',
        key: 'total-revenue-operator-select',
        value: 'gte',
        options: getMathematicalOperatorOptions(),
        can: conditions.isTotalRevenue
      },
      {
        control: 'number',
        name: 'value',
        key: 'total-revenue-value-input',
        placeholder: 'Amount',
        min: 0,
        step: 0.01,
        inputClass: 'w-24',
        can: conditions.isTotalRevenue
      }
    ]
  }
}

export default makePartnerSource
